import { fetchFleetMaintenanceBrakeTests } from "@/api/vtnz-backend/maintenanceBrakeTests";
import { fetchFleetMaintenanceComplianceStatus } from "@/api/vtnz-backend/maintenanceComplianceStatus";
import { fetchFleetMaintenanceLightingChecks } from "@/api/vtnz-backend/maintenanceLightingChecks";
import { fetchFleetMaintenanceRecords } from "@/api/vtnz-backend/maintenanceRecords";
import { fetchFleetMaintenanceTyreTests } from "@/api/vtnz-backend/maintenanceTyreTests";
import { fetchFleetMaintenanceVehicleCondition } from "@/api/vtnz-backend/maintenanceVehicleCondition";
import type { FetchState } from "@/types/sharedTypes";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import type {
  BrakeTestRecord,
  ComplianceStatusRecord,
  FullVehicleInspectionHistory,
  InspectionRecord,
  LightingCheckRecord,
  TyreTestRecord,
  VehicleConditionRecord,
} from "./types";

export const useInspectionHistoryStore = defineStore(
  "inspectionHistoryStore",
  () => {
    // ---------------------------------- State --------------------------------- //

    /** reactive array to hold the fleet's inspection records fetched from the API */
    const records = ref<InspectionRecord[]>([]);

    /** indicates the inspection records fetch's current state */
    const recordsFetchState = ref<FetchState>("idle");
    const recordsFetchErrorMsg = ref<string | null>(null);

    /** reactive array to hold the fleet's brake tests fetched from the API */
    const brakeTests = ref<BrakeTestRecord[]>([]);

    /** indicates the brake tests fetch's current state */
    const brakeTestsFetchState = ref<FetchState>("idle");
    const brakeTestsFetchErrorMsg = ref<string | null>(null);

    /** reactive array to hold the fleet's compliance statuses fetched from the API */
    const complianceStatus = ref<ComplianceStatusRecord[]>([]);

    /** indicates the compliance status fetch's current state */
    const complianceStatusFetchState = ref<FetchState>("idle");
    const complianceStatusFetchErrorMsg = ref<string | null>(null);

    /** reactive array to hold the fleet's lighting checks fetched from the API */
    const lightingChecks = ref<LightingCheckRecord[]>([]);

    /** indicates the lighting checks fetch's current state */
    const lightingChecksFetchState = ref<FetchState>("idle");
    const lightingChecksFetchErrorMsg = ref<string | null>(null);

    /** reactive array to hold the fleet's tyre tests fetched from the API */
    const tyreTests = ref<TyreTestRecord[]>([]);

    /** indicates the tyre tests fetch's current state */
    const tyreTestsFetchState = ref<FetchState>("idle");
    const tyreTestsFetchErrorMsg = ref<string | null>(null);

    /** reactive array to hold the fleet's vehicle condition checks fetched from the API */
    const vehicleConditionChecks = ref<VehicleConditionRecord[]>([]);

    /** indicates the vehicle condition checks fetch's current state */
    const vehicleConditionChecksFetchState = ref<FetchState>("idle");
    const vehicleConditionChecksFetchErrorMsg = ref<string | null>(null);

    // --------------------------------- Getters -------------------------------- //

    /**
     * helper flag to indicate any of this store's fetches are in operation.
     * Good for loading animations. Each source tracks its own `*FetchState`
     * separately (see e.g. `recordsFetchState`) since `fetchInspectionHistory`
     * runs them concurrently and a single shared flag would race.
     */
    const isFetching = computed(
      () =>
        recordsFetchState.value === "loading" ||
        brakeTestsFetchState.value === "loading" ||
        complianceStatusFetchState.value === "loading" ||
        lightingChecksFetchState.value === "loading" ||
        tyreTestsFetchState.value === "loading" ||
        vehicleConditionChecksFetchState.value === "loading",
    );

    /**
     * Returns the inspection records belonging to the given vehicle.
     *
     * `vehicleExternalId` is the vehicle's *external* id (`vehicle_id` on
     * the `maintenance_records` row, matching `Vehicle.externalId`) — not a
     * local/domain id.
     */
    function getRecordsForVehicle(
      vehicleExternalId: string,
    ): InspectionRecord[] {
      return records.value.filter(
        (record) => record.vehicle_id === vehicleExternalId,
      );
    }

    /**
     * Returns the brake tests belonging to the given inspection record.
     *
     * Brake test rows have no `vehicle_id` of their own — they link to
     * their vehicle indirectly via `maintenance_record_id`, so this is
     * looked up per-record rather than per-vehicle.
     */
    function getBrakeTestsForRecord(
      maintenanceRecordId: string,
    ): BrakeTestRecord[] {
      return brakeTests.value.filter(
        (test) => test.maintenance_record_id === maintenanceRecordId,
      );
    }

    /**
     * Returns the compliance statuses belonging to the given vehicle.
     *
     * Unlike brake tests, `maintenance_compliance_status` rows carry their
     * own `vehicle_id`, so this filters directly the same way
     * `getRecordsForVehicle` does.
     */
    function getComplianceStatusForVehicle(
      vehicleExternalId: string,
    ): ComplianceStatusRecord[] {
      return complianceStatus.value.filter(
        (status) => status.vehicle_id === vehicleExternalId,
      );
    }

    /**
     * Returns the lighting checks belonging to the given inspection record.
     *
     * Like brake tests, these rows link to their vehicle indirectly via
     * `maintenance_record_id` — look them up per-record, not per-vehicle.
     */
    function getLightingChecksForRecord(
      maintenanceRecordId: string,
    ): LightingCheckRecord[] {
      return lightingChecks.value.filter(
        (check) => check.maintenance_record_id === maintenanceRecordId,
      );
    }

    /**
     * Returns the tyre tests belonging to the given inspection record.
     *
     * Like brake tests, these rows link to their vehicle indirectly via
     * `maintenance_record_id` — look them up per-record, not per-vehicle.
     */
    function getTyreTestsForRecord(
      maintenanceRecordId: string,
    ): TyreTestRecord[] {
      return tyreTests.value.filter(
        (test) => test.maintenance_record_id === maintenanceRecordId,
      );
    }

    /**
     * Returns the vehicle condition checks belonging to the given
     * inspection record.
     *
     * Like brake tests, these rows link to their vehicle indirectly via
     * `maintenance_record_id` — look them up per-record, not per-vehicle.
     */
    function getVehicleConditionChecksForRecord(
      maintenanceRecordId: string,
    ): VehicleConditionRecord[] {
      return vehicleConditionChecks.value.filter(
        (check) => check.maintenance_record_id === maintenanceRecordId,
      );
    }

    /**
     * Returns the full aggregated inspection history for the given vehicle.
     *
     * A thin wrapper around the individual per-source getters. As more
     * `maintenance_*` sources are piped into this store, fold their
     * per-vehicle (or per-record) getters in here too, so callers keep
     * reading one shape instead of assembling it themselves.
     */
    function getFullHistoryForVehicle(
      vehicleExternalId: string,
    ): FullVehicleInspectionHistory {
      const inspectionRecords = getRecordsForVehicle(vehicleExternalId);
      const recordIds = inspectionRecords.map((record) => record.id);

      return {
        inspectionRecords,
        brakeTests: recordIds.flatMap((id) => getBrakeTestsForRecord(id)),
        complianceStatus: getComplianceStatusForVehicle(vehicleExternalId),
        lightingChecks: recordIds.flatMap((id) =>
          getLightingChecksForRecord(id),
        ),
        tyreTests: recordIds.flatMap((id) => getTyreTestsForRecord(id)),
        vehicleConditionChecks: recordIds.flatMap((id) =>
          getVehicleConditionChecksForRecord(id),
        ),
      };
    }

    // --------------------------------- Actions -------------------------------- //

    /** fetches the fleet's inspection records from the backend and assigns them to state. */
    async function fetchInspectionRecords() {
      recordsFetchState.value = "loading";
      recordsFetchErrorMsg.value = null;

      const fetchRes = await fetchFleetMaintenanceRecords();

      if (!fetchRes.success) {
        recordsFetchErrorMsg.value = fetchRes.errorMsg;
        recordsFetchState.value = "error";
        return;
      }

      records.value = fetchRes.data;
      recordsFetchState.value = "success";
    }

    /** fetches the fleet's brake tests from the backend and assigns them to state. */
    async function fetchBrakeTests() {
      brakeTestsFetchState.value = "loading";
      brakeTestsFetchErrorMsg.value = null;

      const fetchRes = await fetchFleetMaintenanceBrakeTests();

      if (!fetchRes.success) {
        brakeTestsFetchErrorMsg.value = fetchRes.errorMsg;
        brakeTestsFetchState.value = "error";
        return;
      }

      brakeTests.value = fetchRes.data;
      brakeTestsFetchState.value = "success";
    }

    /** fetches the fleet's compliance statuses from the backend and assigns them to state. */
    async function fetchComplianceStatus() {
      complianceStatusFetchState.value = "loading";
      complianceStatusFetchErrorMsg.value = null;

      const fetchRes = await fetchFleetMaintenanceComplianceStatus();

      if (!fetchRes.success) {
        complianceStatusFetchErrorMsg.value = fetchRes.errorMsg;
        complianceStatusFetchState.value = "error";
        return;
      }

      complianceStatus.value = fetchRes.data;
      complianceStatusFetchState.value = "success";
    }

    /** fetches the fleet's lighting checks from the backend and assigns them to state. */
    async function fetchLightingChecks() {
      lightingChecksFetchState.value = "loading";
      lightingChecksFetchErrorMsg.value = null;

      const fetchRes = await fetchFleetMaintenanceLightingChecks();

      if (!fetchRes.success) {
        lightingChecksFetchErrorMsg.value = fetchRes.errorMsg;
        lightingChecksFetchState.value = "error";
        return;
      }

      lightingChecks.value = fetchRes.data;
      lightingChecksFetchState.value = "success";
    }

    /** fetches the fleet's tyre tests from the backend and assigns them to state. */
    async function fetchTyreTests() {
      tyreTestsFetchState.value = "loading";
      tyreTestsFetchErrorMsg.value = null;

      const fetchRes = await fetchFleetMaintenanceTyreTests();

      if (!fetchRes.success) {
        tyreTestsFetchErrorMsg.value = fetchRes.errorMsg;
        tyreTestsFetchState.value = "error";
        return;
      }

      tyreTests.value = fetchRes.data;
      tyreTestsFetchState.value = "success";
    }

    /** fetches the fleet's vehicle condition checks from the backend and assigns them to state. */
    async function fetchVehicleConditionChecks() {
      vehicleConditionChecksFetchState.value = "loading";
      vehicleConditionChecksFetchErrorMsg.value = null;

      const fetchRes = await fetchFleetMaintenanceVehicleCondition();

      if (!fetchRes.success) {
        vehicleConditionChecksFetchErrorMsg.value = fetchRes.errorMsg;
        vehicleConditionChecksFetchState.value = "error";
        return;
      }

      vehicleConditionChecks.value = fetchRes.data;
      vehicleConditionChecksFetchState.value = "success";
    }

    /**
     * Orchestration entry point: fetches every `maintenance_*` source this
     * store covers. This is the single action `appStore`'s bootstrap
     * orchestration should call.
     */
    async function fetchInspectionHistory() {
      await Promise.all([
        fetchInspectionRecords(),
        fetchBrakeTests(),
        fetchComplianceStatus(),
        fetchLightingChecks(),
        fetchTyreTests(),
        fetchVehicleConditionChecks(),
      ]);
    }

    function $reset() {
      recordsFetchState.value = "idle";
      records.value = [];

      brakeTestsFetchState.value = "idle";
      brakeTests.value = [];

      complianceStatusFetchState.value = "idle";
      complianceStatus.value = [];

      lightingChecksFetchState.value = "idle";
      lightingChecks.value = [];

      tyreTestsFetchState.value = "idle";
      tyreTests.value = [];

      vehicleConditionChecksFetchState.value = "idle";
      vehicleConditionChecks.value = [];
    }

    return {
      // --------------------------------- State -------------------------------- //

      /** reactive array to hold the fleet's inspection records fetched from the API */
      records,

      /** indicates the inspection records fetch's current state */
      recordsFetchState,

      /** error message for the last inspection records fetch error, if there is one. */
      recordsFetchErrorMsg,

      /** reactive array to hold the fleet's brake tests fetched from the API */
      brakeTests,

      /** indicates the brake tests fetch's current state */
      brakeTestsFetchState,

      /** error message for the last brake tests fetch error, if there is one. */
      brakeTestsFetchErrorMsg,

      /** reactive array to hold the fleet's compliance statuses fetched from the API */
      complianceStatus,

      /** indicates the compliance status fetch's current state */
      complianceStatusFetchState,

      /** error message for the last compliance status fetch error, if there is one. */
      complianceStatusFetchErrorMsg,

      /** reactive array to hold the fleet's lighting checks fetched from the API */
      lightingChecks,

      /** indicates the lighting checks fetch's current state */
      lightingChecksFetchState,

      /** error message for the last lighting checks fetch error, if there is one. */
      lightingChecksFetchErrorMsg,

      /** reactive array to hold the fleet's tyre tests fetched from the API */
      tyreTests,

      /** indicates the tyre tests fetch's current state */
      tyreTestsFetchState,

      /** error message for the last tyre tests fetch error, if there is one. */
      tyreTestsFetchErrorMsg,

      /** reactive array to hold the fleet's vehicle condition checks fetched from the API */
      vehicleConditionChecks,

      /** indicates the vehicle condition checks fetch's current state */
      vehicleConditionChecksFetchState,

      /** error message for the last vehicle condition checks fetch error, if there is one. */
      vehicleConditionChecksFetchErrorMsg,

      // -------------------------------- Getters ------------------------------- //

      /** helper flag to indicate any of this store's fetches are in operation. Good for loading animations. */
      isFetching,

      /** Returns the inspection records belonging to the given vehicle. */
      getRecordsForVehicle,

      /** Returns the brake tests belonging to the given inspection record. */
      getBrakeTestsForRecord,

      /** Returns the compliance statuses belonging to the given vehicle. */
      getComplianceStatusForVehicle,

      /** Returns the lighting checks belonging to the given inspection record. */
      getLightingChecksForRecord,

      /** Returns the tyre tests belonging to the given inspection record. */
      getTyreTestsForRecord,

      /** Returns the vehicle condition checks belonging to the given inspection record. */
      getVehicleConditionChecksForRecord,

      /** Returns the full aggregated inspection history for the given vehicle. */
      getFullHistoryForVehicle,

      // -------------------------------- Actions ------------------------------- //

      /** fetches the fleet's inspection records from the backend and assigns them to state. */
      fetchInspectionRecords,

      /** fetches the fleet's brake tests from the backend and assigns them to state. */
      fetchBrakeTests,

      /** fetches the fleet's compliance statuses from the backend and assigns them to state. */
      fetchComplianceStatus,

      /** fetches the fleet's lighting checks from the backend and assigns them to state. */
      fetchLightingChecks,

      /** fetches the fleet's tyre tests from the backend and assigns them to state. */
      fetchTyreTests,

      /** fetches the fleet's vehicle condition checks from the backend and assigns them to state. */
      fetchVehicleConditionChecks,

      /** Orchestration entry point: fetches every source this store covers. */
      fetchInspectionHistory,

      /** Resets the store to its initial state. */
      $reset,
    };
  },
);
